import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import HelpIcon from '@mui/icons-material/Help';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SearchOffOutlinedIcon from '@mui/icons-material/SearchOffOutlined';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { sgrColors } from '../../../ui/sgr/SgrColors';

interface StatisticsSectionProps {
  title: ReactNode;
  subtitle?: ReactNode;
  /** When set, a help icon is shown next to the subtitle with this tooltip */
  subtitleTooltip?: ReactNode;
  /** Icon rendered in the tinted tile next to the title */
  icon?: ReactNode;
  /** Optional element aligned to the right of the header, e.g. a results chip */
  headerAdornment?: ReactNode;
  filtersTitle: ReactNode;
  filters: ReactNode;
  /** Called when the user clicks the reset button in the filters header */
  onResetFilters?: () => void;
  isLoading: boolean;
  loadingText: ReactNode;
  isEmpty: boolean;
  emptyText: ReactNode;
  /**
   * 'list' scrolls its content and shows skeleton rows while loading,
   * 'chart' keeps a fixed plot area and never scrolls.
   */
  variant?: 'list' | 'chart';
  children: ReactNode;
}

const CONTENT_HEIGHT = '25rem';

const ListSkeleton = () => (
  <Box sx={{ padding: '0.5rem 1rem' }}>
    {Array.from({ length: 6 }).map((_, index) => (
      <Box
        key={index}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.875rem',
          paddingY: '0.75rem',
        }}
      >
        <Skeleton variant="circular" width="1.75rem" height="1.75rem" />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width={`${70 - index * 7}%`} />
          <Skeleton variant="rounded" height="0.375rem" />
        </Box>
      </Box>
    ))}
  </Box>
);

const StatusOverlay = ({
  icon,
  children,
}: {
  icon: ReactNode;
  children: ReactNode;
}) => (
  <Box
    sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.75rem',
      color: sgrColors.backdrop,
    }}
  >
    {icon}
    <Typography variant="body2" sx={{ color: sgrColors.backdrop }}>
      {children}
    </Typography>
  </Box>
);

const StatisticsSection = ({
  title,
  subtitle,
  subtitleTooltip,
  icon,
  headerAdornment,
  filtersTitle,
  filters,
  onResetFilters,
  isLoading,
  loadingText,
  isEmpty,
  emptyText,
  variant = 'list',
  children,
}: StatisticsSectionProps) => {
  const { t } = useTranslation();
  const isList = variant === 'list';

  const content = isLoading ? (
    isList ? (
      <ListSkeleton />
    ) : (
      <StatusOverlay icon={<CircularProgress size="1.75rem" />}>
        {loadingText}
      </StatusOverlay>
    )
  ) : isEmpty ? (
    <StatusOverlay
      icon={<SearchOffOutlinedIcon sx={{ fontSize: '2.5rem', opacity: 0.6 }} />}
    >
      {emptyText}
    </StatusOverlay>
  ) : (
    children
  );

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: '0.75rem !important',
        border: `1px solid ${sgrColors.grey_cloud}`,
        backgroundColor: '#ffffff',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.875rem',
          padding: '1.25rem 1.5rem',
          borderBottom: `1px solid ${sgrColors.grey_cloud}`,
        }}
      >
        {icon && (
          <Box
            sx={{
              width: '2.5rem',
              height: '2.5rem',
              flexShrink: 0,
              borderRadius: '0.625rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: sgrColors.navy,
              backgroundColor: sgrColors.chips_background,
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: sgrColors.mine_shaft }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="body2"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                color: sgrColors.backdrop,
              }}
            >
              {subtitle}
              {subtitleTooltip && (
                <Tooltip title={subtitleTooltip} arrow>
                  <HelpIcon
                    sx={{
                      fontSize: '1rem',
                      cursor: 'help',
                      color: sgrColors.backdrop,
                      '&:hover': { color: sgrColors.navy },
                    }}
                  />
                </Tooltip>
              )}
            </Typography>
          )}
        </Box>
        {headerAdornment}
      </Box>

      <CardContent sx={{ padding: '1.5rem !important' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: '1.5rem',
            alignItems: 'stretch',
          }}
        >
          <Box
            sx={{
              width: { xs: '100%', md: '65%' },
              height: CONTENT_HEIGHT,
              boxSizing: 'border-box',
              overflowY: isList ? 'auto' : 'hidden',
              border: `1px solid ${sgrColors.grey_cloud}`,
              borderRadius: '0.625rem',
              backgroundColor: sgrColors.frost_grey,
              // Keep the scrollbar unobtrusive so it never competes with the data
              '&::-webkit-scrollbar': { width: '0.5rem' },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: sgrColors.steel,
                borderRadius: '0.25rem',
              },
            }}
          >
            {content}
          </Box>

          <Box
            sx={{
              width: { xs: '100%', md: '35%' },
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              border: `1px solid ${sgrColors.grey_cloud}`,
              borderRadius: '0.625rem',
              backgroundColor: sgrColors.frost_grey,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1rem',
                borderBottom: `1px solid ${sgrColors.grey_cloud}`,
                color: sgrColors.navy,
              }}
            >
              <FilterListRoundedIcon fontSize="small" />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: sgrColors.navy,
                }}
              >
                {filtersTitle}
              </Typography>
              {onResetFilters && (
                <Button
                  size="small"
                  onClick={onResetFilters}
                  startIcon={<RestartAltRoundedIcon fontSize="small" />}
                  sx={{
                    marginLeft: 'auto',
                    color: sgrColors.backdrop,
                    textTransform: 'none',
                    fontWeight: 500,
                    minWidth: 0,
                    padding: '0.125rem 0.5rem',
                    '&:hover': { color: sgrColors.navy },
                  }}
                >
                  {t('entity_statistics.reset_filters')}
                </Button>
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                padding: '1rem',
              }}
            >
              {filters}
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default StatisticsSection;
